import PropTypes from 'prop-types';
import React from 'react';

import cls from 'classnames/bind';

import Button from '@ott/button';
import GdnBanner from '@ott/gdn-banner';
import { Container, Row, Col } from '@ott/grid';
import { l10n, lang } from '@ott/l10n';

import DealCard from './blocks/DealCard';
import DealCardProductWrap from './blocks/DealCardProductWrap';
import { DEALS_LIMIT, MIN_DEALS_BANNER_COUNT, PRODUCTS, TYPES } from './constants';
import getDeals from './utility/getDeals';

import Cookie from 'js-cookie';

import s from './Deals.scss';

const countryСode = {
  de: 'BER',
  'en-db': 'LON',
  'end-ie': 'DUB',
  'es-us': 'NYC',
  es: 'MAD',
  pl: 'WAW',
  ru: 'MOW',
  'ru-by': 'MSQ',
  'ru-kz': 'TSE',
  kz: 'TSE',
  'ru-ua': 'IEV',
  tr: 'IST',
  'uk-ua': 'IEV',
  'ru-us': 'MOW',
};

const cx = cls.bind(s);

const MOBILE_VISIBLE_COUNT = 3;
const TABLET_VISIBLE_COUNT = 4;
const DESKTOP_VISIBLE_COUNT = 6;

const DEFAULT_RAILWAYS_DEALS_COUNT = 6;
const DEFAULT_DEALS_COUNT = 12;

class Deals extends React.PureComponent {
  static propTypes = {
    product: PropTypes.oneOf(Object.values(PRODUCTS)),
    endpointUrl: PropTypes.string,
    from: PropTypes.string,
    title: PropTypes.node,
    titleComment: PropTypes.node,
    staticDeals: PropTypes.arrayOf(PropTypes.oneOfType(PropTypes.object)),
    banner: PropTypes.shape({}),
    getHref: PropTypes.func,
    referrer: PropTypes.string,
    currency: PropTypes.string,
    locale: PropTypes.string,
    onClick: PropTypes.func,
    trackClick: PropTypes.func,
    trackShow: PropTypes.func,
    className: PropTypes.string,
    openNewWindow: PropTypes.bool,
    pageInfo: PropTypes.shape({
      deviceInfo: PropTypes.shape({
        isDesktop: PropTypes.bool,
        isMobile: PropTypes.bool,
        isTablet: PropTypes.bool,
        isTouchDevice: PropTypes.bool,
      }),
    }),
    specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  };

  static defaultProps = {
    product: PRODUCTS.avia,
    endpointUrl: '',
    from: countryСode[lang] || 'MOW',
    title: '',
    titleComment: null,
    staticDeals: null,
    getHref: null,
    referrer: '',
    currency: '',
    locale: '',
    pageInfo: {
      deviceInfo: {},
    },
    specificLocators: null,
    onClick: () => {},
    trackClick: () => {},
    trackShow: () => {},
  };

  setRef = (ref) => {
    this.titleNode = ref;
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      maxCardsCount: this.getVisibleCardCount(props),
      bannerIsEmpty: false,
      bannerIsRendered: false,
      loading: true,
      error: false,
      items: [],
      isMoreClicked: false,
    };
  }

  componentDidMount() {
    const { product } = this.props;

    this.getDefaultImages(product);

    if (__WHITE_LABEL__) {
      this.loadDeals(DEALS_LIMIT);

      return;
    }

    this.checkVisibility(() => {
      this.loadDeals(DEALS_LIMIT);
    });
  }

  componentDidUpdate(prevProps) {
    const { endpointUrl, product, from, getHref, staticDeals, maxCardsCount, banner } = this.props;
    const { items, bannerIsEmpty } = this.state;

    if (prevProps.product !== product) {
      this.getDefaultImages(product);
    }

    if (
      endpointUrl !== prevProps.endpointUrl ||
      product !== prevProps.product ||
      from !== prevProps.from ||
      getHref !== prevProps.getHref ||
      staticDeals !== prevProps.staticDeals
    ) {
      this.loadDeals();
    }

    const hasItems = items && items.length && maxCardsCount !== prevProps.maxCardsCount;
    const canDisplayBanner =
      !bannerIsEmpty &&
      !items.some((item) => item.type === TYPES.advertisement) &&
      banner &&
      items.length >= this.getMinDealsBannerCount();

    if (hasItems && canDisplayBanner) {
      const newItems = this.injectBannerItem(items);
      this.setState({
        items: newItems,
      });
    }
  }

  checkVisibility(getDealsAction) {
    try {
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          getDealsAction();
          io.unobserve(this.titleNode);
        }
      }, {});

      // TODO повесить observable реф на контейнер дилзов, а не на тайтл
      // сейчас возникает баг, что если скроллить дилзы снизу и не дойти до тайтла,
      // то дилзы не начинают прогружаться
      io.observe(this.titleNode);
    } catch (e) {
      getDealsAction();
    }
  }

  injectBannerItem(items) {
    const newItems = [...items];
    const { banner } = this.props;

    const { bannerOptions, ...restBannerProps } = banner.bannerProps;

    let newBannerOptions = {
      ...bannerOptions,
    };
    const source = Cookie.get('referrer');
    if (source) {
      newBannerOptions = { source: [`source_${source}`], ...bannerOptions };
    }

    const bannerItemData = {
      bannerProps: {
        ...restBannerProps,
        bannerOptions: {
          ...newBannerOptions,
        },
      },
      key: banner.key,
      type: TYPES.advertisement,
    };

    const bannerPosition = this.getMinDealsBannerCount();
    Object.assign(newItems[bannerPosition - 1], bannerItemData);

    return newItems;
  }

  getVisibleCardCount({
    pageInfo: {
      deviceInfo: { isTablet, isMobile },
    },
  }) {
    if (isMobile) {
      return MOBILE_VISIBLE_COUNT;
    }

    if (isTablet) {
      return TABLET_VISIBLE_COUNT;
    }

    return DESKTOP_VISIBLE_COUNT;
  }

  getMinDealsBannerCount() {
    const {
      pageInfo: {
        deviceInfo: { isTablet, isMobile },
      },
    } = this.props;

    if (isTablet) {
      return MIN_DEALS_BANNER_COUNT.tablet;
    }

    if (isMobile) {
      return MIN_DEALS_BANNER_COUNT.mobile;
    }

    return MIN_DEALS_BANNER_COUNT.desktop;
  }

  lastLoadDealsTime = null;

  loadDeals(limit) {
    const {
      endpointUrl,
      product,
      from,
      getHref,
      staticDeals,
      trackShow,
      banner,
      referrer,
      currency,
      locale,
    } = this.props;

    this.setState({
      maxCardsCount: this.getVisibleCardCount(this.props),
      loading: true,
      error: false,
    });

    trackShow();

    const loadDealsTime = Date.now();
    this.lastLoadDealsTime = loadDealsTime;

    getDeals({
      endpointUrl,
      product,
      from,
      staticDeals,
      getHref,
      referrer,
      currency,
      locale,
      limit,
    })
      .then((data) => {
        if (loadDealsTime === this.lastLoadDealsTime) {
          let items = data.items;
          if (banner && data.items.length >= this.getMinDealsBannerCount()) {
            items = this.injectBannerItem(items);
          }

          this.setState(() => ({
            loading: false,
            items,
            isMoreClicked: items.length > DEALS_LIMIT,
          }));
        }
      })
      .catch((error) => {
        if (loadDealsTime === this.lastLoadDealsTime) {
          this.setState({
            loading: false,
            error,
            items: [],
          });
        }
      });
  }

  getDefaultImages(product) {
    let defaultImageUrls;

    if (product === PRODUCTS.poezda) {
      defaultImageUrls = new Array(DEFAULT_RAILWAYS_DEALS_COUNT)
        .fill(0)
        .map((_, index) => `//static.onetwotrip.com/deals/cities/default/trainV2_${index + 1}.jpg`);
    } else {
      defaultImageUrls = new Array(DEFAULT_DEALS_COUNT)
        .fill(0)
        .map((_, index) => `//static.onetwotrip.com/deals/cities/default/flight${index + 1}.jpg`);
    }

    defaultImageUrls = defaultImageUrls.sort(() => Math.round(Math.random() * 2) - 1);

    this.setState({
      defaultImageUrls,
    });
  }

  handleBannerDestroy(bannerKey, bannerIsEmpty) {
    const { items } = this.state;

    if (bannerIsEmpty) {
      const newItems = items.map((item) => {
        if (item.type === TYPES.advertisement) {
          const { bannerProps, key, type, ...restItemProps } = item;

          return {
            ...restItemProps,
            type: TYPES.deal,
          };
        }

        return item;
      });
      this.setState({
        bannerIsEmpty,
        items: newItems,
      });
    }
  }

  handleBannerRender() {
    this.setState({
      bannerIsRendered: true,
    });
  }

  handleShowMoreClick = () => {
    const { loading, isMoreClicked } = this.state;

    if (loading) {
      return;
    }

    const visibleCount = this.getVisibleCardCount(this.props);

    if (!isMoreClicked) {
      this.loadDeals();
    }

    this.setState((state) => ({
      maxCardsCount: state.maxCardsCount + visibleCount,
    }));
  };

  renderMoreButton() {
    const { items, maxCardsCount, isMoreClicked, loading } = this.state;
    const { product } = this.props;

    const hasHiddenDeals = maxCardsCount < items.length;
    const shouldShowMoreButton = items && (hasHiddenDeals || !isMoreClicked);

    // делаем отдельное условие для басов, т.к. в басах дилзы захардкожены,
    // из-за чего флаг isMoreClicked в басах работает не корректно и кнопка все время показывается
    if (product === PRODUCTS.bus && !hasHiddenDeals) {
      return null;
    }

    if (!shouldShowMoreButton) {
      return null;
    }

    return (
      <Row>
        <Col desktop={12} mobile={4}>
          <Button
            className={cx('showMoreButton')}
            purpose="neutral"
            onClick={this.handleShowMoreClick}
            specificLocators={['show-more-button']}
            isLoading={loading}
          >
            {l10n('deals.moreVariants')}
          </Button>
        </Col>
      </Row>
    );
  }

  renderTitleComment() {
    const { titleComment } = this.props;

    if (!titleComment) {
      return null;
    }

    return <div className={cx('titleComment')}>{titleComment}</div>;
  }

  renderItems() {
    const { product, onClick, trackClick, openNewWindow } = this.props;
    const { items, loading, maxCardsCount, defaultImageUrls, bannerIsRendered } = this.state;
    let itemsNodes;

    if (loading && !items.length) {
      itemsNodes = new Array(maxCardsCount).fill(null).map((n, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Col key={index} desktop={4} mobile={4}>
            <DealCard loading={true} />
          </Col>
        );
      });

      return <Row>{itemsNodes}</Row>;
    }

    if (!items || items.length === 0 || defaultImageUrls == null) {
      return [];
    }

    itemsNodes = items.slice(0, maxCardsCount).map((item, index) => {
      const defaultBackgroundImageUrl = defaultImageUrls[index % defaultImageUrls.length];

      if (item.type === TYPES.advertisement) {
        const dealCard = !bannerIsRendered && (
          <DealCardProductWrap
            onClick={onClick}
            product={product}
            defaultBackgroundImageUrl={defaultBackgroundImageUrl}
            data={item}
            trackClick={trackClick}
            openNewWindow={openNewWindow}
          />
        );

        return (
          <Col key={item.key} desktop={4} mobile={4}>
            <GdnBanner
              // eslint-disable-next-line react/jsx-no-bind
              onDestroy={this.handleBannerDestroy.bind(this, item.key)}
              // eslint-disable-next-line react/jsx-no-bind
              onRender={this.handleBannerRender.bind(this)}
              className={cx('banner', { 'banner--isHidden': !bannerIsRendered })}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...item.bannerProps}
            />
            {dealCard}
          </Col>
        );
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <Col key={index} desktop={4} mobile={4}>
          <DealCardProductWrap
            onClick={onClick}
            product={product}
            defaultBackgroundImageUrl={defaultBackgroundImageUrl}
            data={item}
            trackClick={trackClick}
            openNewWindow={openNewWindow}
          />
        </Col>
      );
    });

    return <Row>{itemsNodes}</Row>;
  }

  render() {
    const { className, product, title, specificLocators } = this.props;
    const { error, items, loading } = this.state;

    if (error) {
      return null;
    }

    if (!loading && !items.length) {
      return null;
    }

    const smartTitle = title || l10n(`deals.title.${product}`);
    const titleComment = this.renderTitleComment();
    const moreButtonNode = this.renderMoreButton();
    const dealsItemNodes = this.renderItems();

    return (
      <Container
        data-locator={cls('Deals', specificLocators)}
        className={cls(cx('Deals'), className)}
      >
        <Row>
          <Col desktop={12} mobile={4}>
            <h2 className={cx('title')} ref={this.setRef}>
              {smartTitle}
            </h2>
          </Col>
          <Col desktop={12} mobile={4}>
            {titleComment}
          </Col>
        </Row>
        {dealsItemNodes}
        {moreButtonNode}
      </Container>
    );
  }
}

export default Deals;
